import React, { useCallback } from "react";
import { useMutation } from "react-query";
import { useLocation } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { Card } from "@mui/material";

import { useDocumentActionsStatus } from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import {
  IPdfFile,
  IDocumentVersion,
  getPdfDocumentById,
  getPdfDocumentMetaById,
} from "utils/DexieDBManager";
import { STARRED_ACTION_TYPE } from "utils/DexieDBManager/dexieDB.models";
import DialogAlertMessage from "Shared.Components/DialogAlertMessage";
import { computeErrorMessage } from "utils/utils";

import PdfDocumentCardActionArea from "./PdfDocumentCardActionArea";
import PdfDocumentCardActions from "./PdfDocumentCardActions";
import { styledPdfCardItem } from "./stylesPdfDocumentCards";

import PdfViewerModal from "../PdfDocumentViewer";
import {
  addPdfDocumentToFavoritesAsync,
  downloadPdfFileBinaryByIdAsync,
  deletePdfDocumentFromFavoritesAsync,
} from "../pdfDocuments.service";

interface IPdfDocumentCardProps {
  documentVersion: IDocumentVersion;
}

export const PdfDocumentCard: React.FunctionComponent<
  IPdfDocumentCardProps
> = ({ documentVersion }) => {
  const [documentPdfFile, setDocumentPdfFile] = React.useState<IPdfFile>();
  const [isOpenDialogAlert, setOpenDialogAlert] = React.useState(false);
  const [isViewerOpen, setIsViewerOpen] = React.useState(false);
  const { state, dispatch } = useDocumentActionsStatus();
  const { pathname } = useLocation();

  const documentPdfFileMeta = useLiveQuery(
    () => getPdfDocumentMetaById(documentVersion.file.id),
    [documentVersion.file.id]
  );

  const {
    data: downloadPdfFileBinaryByIdData,
    isLoading: isDownloadPdfFileLoading,
    mutate: downloadPdfFileBinaryById,
  } = useMutation(() =>
    downloadPdfFileBinaryByIdAsync(
      documentVersion.file,
      documentVersion.parentDocument.offline
    )
  );

  const parentDocumentId = documentVersion.parentDocument.id;
  const {
    data: addPdfDocumentToFavoritesData,
    isLoading: isAddingPdfDocumentToFavorites,
    mutate: addPdfDocumentToFavorites,
  } = useMutation(() =>
    addPdfDocumentToFavoritesAsync({
      synchronizationId: documentVersion.parentDocument.category.id,
      documentVersionType: documentVersion.applicableStateName,
      isAckRequired: documentVersion.uds.isAck,
      fileId: documentVersion.file.id,
      parentDocumentId,
    })
  );

  const {
    data: deletePdfDocumentFromFavoritesData,
    isLoading: isDeletingPdfDocumentFromFavorites,
    mutate: deletePdfDocumentFromFavorites,
  } = useMutation(() =>
    deletePdfDocumentFromFavoritesAsync({
      synchronizationId: documentVersion.parentDocument.category.id,
      documentVersionType: documentVersion.applicableStateName,
      isAckRequired: documentVersion.uds.isAck,
      fileId: documentVersion.file.id,
      parentDocumentId,
    })
  );

  const handleReadPdfDocument = async () => {
    setIsViewerOpen(true);

    if (
      !documentPdfFileMeta?.isBufferSavedInDB ||
      documentPdfFileMeta === undefined ||
      null
    ) {
      downloadPdfFileBinaryById();
    } else {
      const documentPdfFile = await getPdfDocumentById(documentVersion.file.id);
      setDocumentPdfFile(documentPdfFile);
    }
  };

  const handleDownloadPdfDocument = () => {
    if (!documentPdfFileMeta?.isBufferSavedInDB) {
      downloadPdfFileBinaryById();
    }
  };

  const closePdfViewer = React.useCallback(() => {
    setIsViewerOpen(false);
  }, []);

  const isStarredDocument = Boolean(
    state.documentActionsStatus.get(parentDocumentId)?.isStarredDocument
  );

  const handleTogglePdfDocumentFavoriteState = useCallback(() => {
    if (!navigator.onLine) {
      return;
    }

    if (isStarredDocument) {
      deletePdfDocumentFromFavorites();
    } else {
      addPdfDocumentToFavorites();
    }

    const synchronizationItemToUpdate = {
      isAckRequired: documentVersion.uds.isAck,
      documentVersionType: documentVersion.applicableStateName,
      synchronizationId: documentVersion.parentDocument.category.id,
      starredActionType: isStarredDocument
        ? STARRED_ACTION_TYPE.DELETE
        : STARRED_ACTION_TYPE.ADD,
    };

    dispatch({
      type: "updateStarredIsAckRequiredDocumentsState",
      synchronizationItemToUpdate,
    });
  }, [
    deletePdfDocumentFromFavorites,
    addPdfDocumentToFavorites,
    dispatch,
    isStarredDocument,
    documentVersion.uds.isAck,
    documentVersion.applicableStateName,
    documentVersion.parentDocument.category.id,
  ]);

  const computedPdfFileBuffer =
    documentPdfFile?.pdfBuffer || downloadPdfFileBinaryByIdData?.responseData;

  const isTogglingPdfDocumentFavoriteState =
    isAddingPdfDocumentToFavorites || isDeletingPdfDocumentFromFavorites;

  React.useEffect(() => {
    if (
      addPdfDocumentToFavoritesData?.isSuccess ||
      deletePdfDocumentFromFavoritesData?.isSuccess
    ) {
      dispatch({
        type: "toggleIsFavorite",
        parentDocumentId: parentDocumentId,
      });
    }
  }, [
    addPdfDocumentToFavoritesData?.isSuccess,
    deletePdfDocumentFromFavoritesData?.isSuccess,
    parentDocumentId,
    dispatch,
  ]);

  React.useEffect(() => {
    if (
      downloadPdfFileBinaryByIdData?.error ||
      deletePdfDocumentFromFavoritesData?.error
    ) {
      setOpenDialogAlert(true);
    }
  }, [
    downloadPdfFileBinaryByIdData?.error,
    deletePdfDocumentFromFavoritesData?.error,
  ]);

  let apiErrorTypeOrDetail;
  if (downloadPdfFileBinaryByIdData?.error) {
    apiErrorTypeOrDetail = computeErrorMessage(
      downloadPdfFileBinaryByIdData.error
    );
  } else if (deletePdfDocumentFromFavoritesData?.error) {
    apiErrorTypeOrDetail = computeErrorMessage(
      deletePdfDocumentFromFavoritesData.error
    );
  }

  return (
    <>
      <Card
        key={
          documentVersion.id + pathname + documentVersion.applicableStateName
        }
        sx={() => computeCardStyle(pathname)}
      >
        <PdfDocumentCardActionArea
          handleReadPdfDocument={handleReadPdfDocument}
          isDownloadPdfFileLoading={isDownloadPdfFileLoading}
          documentPdfFileMeta={documentPdfFileMeta}
          documentVersion={documentVersion}
        />
        <PdfDocumentCardActions
          handleDownloadPdfDocument={handleDownloadPdfDocument}
          handleTogglePdfDocumentFavoriteState={
            handleTogglePdfDocumentFavoriteState
          }
          isTogglingPdfDocumentFavoriteState={
            isTogglingPdfDocumentFavoriteState
          }
          isDownloadPdfFileLoading={isDownloadPdfFileLoading}
          documentPdfFileMeta={documentPdfFileMeta}
          isStarredDocument={isStarredDocument}
          documentVersion={documentVersion}
        />
      </Card>
      {computedPdfFileBuffer && isViewerOpen && (
        <PdfViewerModal
          onClose={closePdfViewer}
          isViewerOpen={isViewerOpen}
          documentVersion={documentVersion}
          pdfFileBuffer={computedPdfFileBuffer}
        />
      )}
      {isOpenDialogAlert && (
        <DialogAlertMessage
          isOpenDialogAlert={isOpenDialogAlert}
          onClose={() => setOpenDialogAlert(false)}
          apiErrorTypeOrDetail={apiErrorTypeOrDetail}
        />
      )}
    </>
  );
};

interface IPdfDocumentCardsProps {
  documentVersionsToDisplay: IDocumentVersion[];
}

const PdfDocumentCards: React.FunctionComponent<IPdfDocumentCardsProps> = ({
  documentVersionsToDisplay,
}) => {
  return (
    <>
      {documentVersionsToDisplay.map((documentVersion) => (
        <PdfDocumentCard
          key={documentVersion.id}
          documentVersion={documentVersion}
        />
      ))}
    </>
  );
};

export default PdfDocumentCards;

const computeCardStyle = (pathname: string) => {
  return pathname !== "/"
    ? styledPdfCardItem
    : { ...styledPdfCardItem, minWidth: "200px", width: "200px" };
};
